// My Practice Projects Page (Student Enrollments)
// Date: January 21, 2026

import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { EnrollmentCard } from '../../components/PracticeProjects';
import { useMyEnrollmentsQuery } from '../../api/practiceProjectsApi';
import { BookOpen, Clock, CheckCircle } from 'lucide-react';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { ProjectFilters } from '../../components/ProjectFilters';
import paths from '../../routes';
import { useNavigate } from 'react-router-dom';

type FilterTab = 'all' | 'in_progress' | 'completed';

export default function MyPracticeProjectsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // React Query hook with search parameter
  const { data, isLoading, isError } = useMyEnrollmentsQuery({ search: searchQuery });

  const enrollments = data?.data || [];

  // Filter enrollments based on active tab
  const filteredEnrollments = enrollments.filter((enrollment) => {
    if (activeTab === 'all') return true;
    if (activeTab === 'in_progress') return enrollment.status === 'in_progress';
    if (activeTab === 'completed') return enrollment.status === 'completed';
    return true;
  });

  // Counts for badges
  const inProgressCount = enrollments.filter((e) => e.status === 'in_progress').length;
  const completedCount = enrollments.filter((e) => e.status === 'completed').length;

  const handleBrowseProjects = () => {
    navigate(paths.practiceProjects);
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-primary" />
              <h1 className="text-3xl font-bold">My Practice Projects</h1>
            </div>
            <p className="text-muted-foreground text-lg mt-1">
              Track your progress and continue learning
            </p>
          </div>
          <Button onClick={handleBrowseProjects}>
            Browse More Projects
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <ProjectFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          config={{
            showSearch: true,
          }}
          searchPlaceholder="Search by project name, description, or tags..."
        />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-80" />
          ))}
        </div>
      ) : isError ? (
        <div className="text-center py-12 text-destructive">
          Failed to load your enrollments. Please try again.
        </div>
      ) : enrollments.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold mb-2">
            {searchQuery ? 'No matching projects found' : 'No Enrollments Yet'}
          </h3>
          <p className="text-muted-foreground mb-6">
            {searchQuery 
              ? 'Try adjusting your search terms or browse for new projects.' 
              : 'Start your learning journey by enrolling in practice projects.'}
          </p>
          <Button onClick={handleBrowseProjects}>
            <BookOpen className="w-4 h-4 mr-2" />
            Browse Practice Projects
          </Button>
        </div>
      ) : (
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as FilterTab)}>
          <TabsList>
            <TabsTrigger value="all">
              All
              <Badge variant="secondary" className="ml-2">
                {enrollments.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="in_progress">
              <Clock className="w-4 h-4 mr-2" />
              In Progress
              {inProgressCount > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {inProgressCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="completed">
              <CheckCircle className="w-4 h-4 mr-2" />
              Completed
              {completedCount > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {completedCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            {filteredEnrollments.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">
                  {searchQuery 
                    ? `No ${activeTab === 'in_progress' ? 'in progress' : 'completed'} projects match your search`
                    : `No ${activeTab === 'in_progress' ? 'in progress' : 'completed'} projects`}
                </p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredEnrollments.map((enrollment) => (
                  <EnrollmentCard
                    key={enrollment.id}
                    enrollment={enrollment}
                    showProjectDetails={true}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
